// Paneles de solo lectura de una nota: crédito y aprobaciones (D-108), reserva por línea (1F-C),
// auditoría visual y trazabilidad Nota → Reserva → Despacho → DTE. Todo viene del backend.
import { useDespachosDeNota, useDocumentacion } from '../api/consultas';
import { PERMISO_DE_AMBITO, type Nota } from '../api/contratos';
import { EstadoCredito } from '../comun/Estados';
import { decimal, esCero, fechaHora, NOMBRE_CONDICION, pesos } from '../comun/formato';
import { MOTIVOS_CREDITO, NOMBRE_AMBITO } from '../errores/catalogo';
import { useSesion } from '../sesion/SesionContexto';

/**
 * Quién hizo algo: el nombre (si el backend lo informó, p. ej. en la nota) o, si no, el identificador
 * corto (deuda D-UI-4).
 */
export function useNombreActor(nombres?: Record<string, string>) {
  const { usuario } = useSesion();
  return (id: string | null | undefined) =>
    !id
      ? '—'
      : id === usuario?.id
        ? `${usuario.nombre} (usted)`
        : (nombres?.[id] ?? `usuario ${id.slice(-6)}`);
}

export function PanelCredito({ nota }: { nota: Nota }) {
  const actor = useNombreActor(nota.nombres);
  const decision = nota.credito.find((c) => c.revision === nota.revision) ?? nota.credito[0];
  const solicitudes = nota.solicitudes;
  if (!decision && !solicitudes.length) return null;
  return (
    <section className="panel" aria-label="Crédito y aprobaciones">
      <h3>Crédito y aprobaciones</h3>
      {decision && (
        <div className="fila-datos">
          <EstadoCredito resultado={decision.resultado} />
          <span>
            {NOMBRE_CONDICION[decision.condicion_pago] ?? decision.condicion_pago}
            {decision.plazo_dias ? ` · ${decision.plazo_dias} días` : ''}
          </span>
          <span>Monto {pesos(decision.monto)}</span>
          <span>Cupo {decision.cupo ? pesos(decision.cupo) : 'sin línea'}</span>
          <span className="apagado">
            Fuente CxC: {decision.fuente === 'NINGUNA' ? 'ninguna' : decision.fuente}
          </span>
        </div>
      )}
      {decision?.motivos.length ? (
        <ul className="motivos">
          {decision.motivos.map((m) => (
            <li key={m} data-motivo={m}>
              {m === 'SIN_FUENTE_CXC' ? <strong>Esta venta requiere autorización.</strong> : null}{' '}
              {MOTIVOS_CREDITO[m] ?? m}
            </li>
          ))}
        </ul>
      ) : null}
      {solicitudes.length > 0 && (
        <table className="grilla compacta" aria-label="Solicitudes de aprobación">
          <thead>
            <tr>
              <th>Motivo (ámbito)</th>
              <th>Debe aprobar</th>
              <th>Estado</th>
              <th>Pedida</th>
              <th>Resuelta</th>
            </tr>
          </thead>
          <tbody>
            {solicitudes.map((s) => (
              <tr key={s.id} data-solicitud={s.estado}>
                <td>{s.ambitos.map((a) => NOMBRE_AMBITO[a] ?? a).join(', ')}</td>
                <td>
                  {s.ambitos.map((a) => (
                    <code key={a}>{PERMISO_DE_AMBITO[a] ?? a}</code>
                  ))}
                  {s.exige_segregacion && <span className="apagado"> · no quien la pidió</span>}
                </td>
                <td>
                  <span
                    className={`insignia tono-${s.estado === 'APROBADA' ? 'ok' : s.estado === 'PENDIENTE' ? 'aviso' : 'malo'}`}
                  >
                    {s.estado}
                  </span>
                </td>
                <td>
                  {fechaHora(s.creado_en)} · {actor(s.solicitada_por)}
                </td>
                <td>
                  {s.resuelta_en ? `${fechaHora(s.resuelta_en)} · ${actor(s.resuelta_por)}` : '—'}
                  {s.motivo_resolucion && <div className="apagado">{s.motivo_resolucion}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** Solicitado / reservado / pendiente por línea. La reserva parcial se muestra, no se oculta. */
export function PanelReserva({ nota }: { nota: Nota }) {
  const r = nota.reservas;
  if (!['CONFIRMADA', 'CERRADA_CORTA'].includes(nota.estado)) return null;
  const decision = r.decisiones[r.decisiones.length - 1];
  return (
    <section className="panel" aria-label="Reserva">
      <h3>
        Reserva{' '}
        <span className={`insignia tono-${esCero(r.pendiente) ? 'ok' : 'aviso'}`}>
          {esCero(r.pendiente) ? 'Total' : 'Parcial con pendiente'}
        </span>
        {decision && <span className="apagado"> · política {decision.modo}</span>}
      </h3>
      <table className="grilla compacta" aria-label="Reserva por línea">
        <thead>
          <tr>
            <th>#</th>
            <th>Producto</th>
            <th className="al-der">Solicitado</th>
            <th className="al-der">Reservado</th>
            <th className="al-der">Pendiente</th>
            <th className="al-der">Despachado</th>
          </tr>
        </thead>
        <tbody>
          {r.lineas.map((l) => {
            const ln = nota.lineas.find((x) => x.numero === l.numero);
            const unidad = 'u. base';
            return (
              <tr key={l.lineaId} data-reserva-linea={l.numero}>
                <td className="mono">{l.numero}</td>
                <td>
                  {ln?.descripcion ?? l.productoId}
                  {ln && ln.presentacionCodigo && (
                    <span className="apagado">
                      {' '}
                      · {decimal(ln.cantidad)} {ln.presentacionCodigo}
                    </span>
                  )}
                </td>
                <td className="al-der mono">
                  Solicitado: {decimal(l.requerida)} <span className="apagado">{unidad}</span>
                </td>
                <td className="al-der mono">Reservado: {decimal(l.reservada)}</td>
                <td className={`al-der mono ${esCero(l.pendiente) ? '' : 'texto-aviso fuerte'}`}>
                  Pendiente: {decimal(l.pendiente)}
                </td>
                <td className="al-der mono">{decimal(l.despachada)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2}>Total (unidad base)</td>
            <td className="al-der mono">{decimal(r.requerido)}</td>
            <td className="al-der mono">{decimal(r.reservado)}</td>
            <td className="al-der mono fuerte">{decimal(r.pendiente)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </section>
  );
}

const ACCION: Record<string, string> = {
  crear: 'Creada',
  editar: 'Editada',
  confirmar: 'Confirmada',
  enviar_aprobacion: 'Enviada a aprobación',
  aprobar: 'Aprobada',
  rechazar: 'Rechazada',
  retirar_aprobacion: 'Aprobación retirada',
  descartar: 'Descartada',
  revisar: 'Revisada',
  cerrar_corta: 'Cerrada corta',
  anular: 'Anulada',
};

export function PanelAuditoria({ nota }: { nota: Nota }) {
  const actor = useNombreActor(nota.nombres);
  return (
    <section className="panel" aria-label="Auditoría">
      <h3>Auditoría</h3>
      <table className="grilla compacta">
        <thead>
          <tr>
            <th>Acción</th>
            <th>Por</th>
            <th>Fecha y hora</th>
            <th>Rev.</th>
            <th>Motivo</th>
          </tr>
        </thead>
        <tbody>
          {nota.historia.map((h) => (
            <tr key={h.id}>
              <td>{ACCION[h.accion] ?? h.accion}</td>
              <td>{actor(h.actor_usuario_id)}</td>
              <td>{fechaHora(h.en)}</td>
              <td className="mono">{h.revision}</td>
              <td>{h.motivo ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export function PanelTrazabilidad({
  nota,
  alAbrirDespacho,
}: {
  nota: Nota;
  /** Solo para perfiles de bodega/logística: el puesto comercial no administra despachos. */
  alAbrirDespacho?: (() => void) | undefined;
}) {
  const { empresa, puede } = useSesion();
  const confirmada = ['CONFIRMADA', 'CERRADA_CORTA'].includes(nota.estado);
  const despachos = useDespachosDeNota(empresa!.id, confirmada ? nota.notaId : null);
  const doc = useDocumentacion(empresa!.id, nota.notaId, confirmada && puede('dte.ver'));
  const pend = nota.reservas.pendiente;
  return (
    <section className="panel traza" aria-label="Trazabilidad">
      <h3>Trazabilidad</h3>
      <ol className="traza-pasos">
        <li className="hecho">
          <strong>Nota</strong> {nota.numeroVisible}
        </li>
        <li className={confirmada ? 'hecho' : ''}>
          <strong>Reserva</strong>{' '}
          {confirmada
            ? `${decimal(nota.reservas.reservado)} reservado · ${decimal(pend)} pendiente`
            : '—'}
        </li>
        <li className={despachos.data?.items.some((d) => d.estado === 'CONFIRMADO') ? 'hecho' : ''}>
          <strong>Despacho</strong>{' '}
          {despachos.data?.items.length
            ? despachos.data.items.map((d) => `#${d.numero} ${d.estado}`).join(' · ')
            : confirmada
              ? 'sin despachos'
              : '—'}
          {confirmada && alAbrirDespacho && (
            <button type="button" className="enlace" onClick={alAbrirDespacho}>
              Ir a despacho
            </button>
          )}
        </li>
        <li className={doc.data?.documentos.length ? 'hecho' : ''}>
          <strong>DTE</strong>{' '}
          {!puede('dte.ver')
            ? 'sin permiso para ver'
            : doc.data?.documentos.length
              ? doc.data.documentos
                  .map((d) =>
                    d.dte
                      ? `${d.dte.tipoDte} ${d.dte.folio ? `folio ${d.dte.folio}` : 'sin folio'}`
                      : d.tipoDocumento,
                  )
                  .join(' · ')
              : confirmada
                ? 'sin documento tributario'
                : '—'}
        </li>
      </ol>
    </section>
  );
}
